import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import API from '../services';
import { setBrowser, setIsLoggedIn } from '../store/authReducer';
import { FaTelegramPlane } from 'react-icons/fa';
import { getTelegramBotLink } from '../constants/whitelabelEnv';
import { domain } from '../constants/Branding';

// OTP length constant
const OTP_LENGTH = 6;
interface RegisterPageProps {
  theme?: 'default' | 'lovable';
  onSwitchToLogin?: () => void;
  referalCode?: string;
}

const RegisterPage: React.FC<RegisterPageProps> = ({ theme = 'default', onSwitchToLogin, referalCode }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Form data
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    campaignCode: referalCode || '',
    mobileNumber: '',
    countryCode: '91',
  });
  
  // OTP state
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [otpToken, setOtpToken] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  
  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const usernameCheckTimeout = useRef<NodeJS.Timeout | null>(null);

  const telegramLink = getTelegramBotLink(domain);
  const telegramLinkWithReferral = referalCode ? `${telegramLink}?start=${referalCode}` : telegramLink;
  
  // Theme-based classes
  const isLovable = theme === 'lovable';
  const containerBg = isLovable 
    ? 'bg-[hsl(275_35%_16%)] border border-[hsl(275,90%,64%)]' 
    : 'bg-zinc-900';
  const containerRounded = isLovable ? 'rounded-2xl' : 'rounded-lg';
  const innerBg = isLovable 
    ? 'bg-[hsl(275,35%,16%)] border-[hsl(275,90%,64%)]' 
    : 'bg-zinc-800 border-zinc-700';
  const inputClass = isLovable 
    ? 'bg-[hsl(275,35%,12%)] text-white border-[hsl(275,90%,64%)] focus:ring-[hsl(275,90%,64%)] placeholder:text-[hsl(275_50%_65%)]' 
    : 'bg-zinc-800 text-white border-zinc-700 focus:ring-blue-500';
  const primaryBtnClass = isLovable 
    ? 'bg-[hsl(275,90%,72%)] hover:bg-[hsl(275,90%,78%)] text-black font-semibold' 
    : 'bg-blue-600 hover:bg-blue-700 text-white';
  const countryCodeClass = isLovable 
    ? 'bg-[hsl(275,35%,20%)] text-[hsl(275_50%_65%)] border-[hsl(275,90%,64%)]' 
    : 'bg-zinc-700 text-white border-zinc-700';
  const linkClass = isLovable ? 'text-[hsl(275,90%,64%)]' : 'text-blue-400';
  const iconClass = isLovable ? 'text-[hsl(275_50%_65%)]' : 'text-white';
  const descriptionTextClass = isLovable ? 'text-[hsl(275_50%_65%)]' : 'text-zinc-400';
  const otpInputClass = isLovable
    ? 'border-[hsl(275,90%,64%)] bg-[hsl(275,35%,12%)]'
    : 'border-zinc-700 bg-zinc-800';
  const otpFilledClass = isLovable
    ? 'border-[hsl(275,90%,64%)] bg-[hsl(275,35%,20%)]'
    : 'border-blue-500 bg-blue-900/20';
  
  const handleSignInClick = () => {
    if (onSwitchToLogin) {
      onSwitchToLogin();
    } else {
      navigate('/login');
    }
  };
  
  // Timer effect for OTP
  useEffect(() => {
    if (timeRemaining <= 0) return;
    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timeRemaining]);

  // Focus first OTP input when OTP is sent
  useEffect(() => {
    if (otpToken && timeRemaining > 0) {
      inputsRef.current[0]?.focus();
    }
  }, [otpToken]);

  // Validation functions
  const validateUsername = (value: string) => {
    if (!value.trim()) return 'Required';
    if (!/^[a-zA-Z0-9]+$/.test(value)) return 'Only letters and numbers allowed (no symbols)';
    if (value.length < 5) return `UserId must be at least 5 characters (${value.length}/5)`;
    return '';
  };

  // Check if username exists
  const checkUsernameAvailability = async (username: string) => {
    if (username.length < 5) {
      setUsernameAvailable(null);
      return;
    }

    setUsernameChecking(true);
    try {
      const response = await API.post(`/auth/is-existing-user-by-id`, {
        userId: username,
      });

      const data = response.data;
      if (data.status === "RS_OK") {
        // If user exists, username is NOT available
        setUsernameAvailable(!data.isExistingUser);
        if (data.isExistingUser) {
          setErrors(prev => ({ ...prev, username: 'UserId already taken' }));
        } else {
          setErrors(prev => {
            const newErrors = { ...prev };
            if (newErrors.username === 'UserId already taken') {
              delete newErrors.username;
            }
            return newErrors;
          });
        }
      }
    } catch (error) {
      console.error("Failed to check username:", error);
    } finally {
      setUsernameChecking(false);
    }
  };

  const validatePassword = (password: string) => {
    if (!password) return 'Required';
    
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push(`min 8 chars (${password.length}/8)`);
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('lowercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('number');
    }
    
    if (errors.length > 0) {
      return `Missing: ${errors.join(', ')}`;
    }
    return '';
  };

  const validateMobileNumber = (value: string) => {
    if (!value) return 'Required';
    if (!/^\d{10}$/.test(value)) return 'Enter valid 10-digit mobile number';
    return '';
  };

  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Limit mobile number to 10 digits
    if (name === 'mobileNumber' && value.length > 10) return;
    
    // For username: only allow alphanumeric
    if (name === 'username') {
      // Remove any non-alphanumeric characters
      const cleanValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setFormData(prev => ({ ...prev, [name]: cleanValue }));
      
      // Reset availability status when typing
      setUsernameAvailable(null);
      
      // Clear previous timeout
      if (usernameCheckTimeout.current) {
        clearTimeout(usernameCheckTimeout.current);
      }
      
      // Validate in real-time (show error if less than 5 chars)
      if (cleanValue.length > 0 && cleanValue.length < 5) {
        setErrors(prev => ({ ...prev, username: `UserId must be at least 5 characters (${cleanValue.length}/5)` }));
      } else if (cleanValue.length >= 5) {
        // Clear validation error but keep "already taken" error if present
        setErrors(prev => {
          const newErrors = { ...prev };
          if (newErrors.username && newErrors.username !== 'UserId already taken') {
            delete newErrors.username;
          }
          return newErrors;
        });
        // Debounce the API call
        usernameCheckTimeout.current = setTimeout(() => {
          checkUsernameAvailability(cleanValue);
        }, 500);
      } else {
        // Empty - clear error
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.username;
          return newErrors;
        });
      }
      return;
    }
    
    // For password: validate in real-time
    if (name === 'password') {
      setFormData(prev => ({ ...prev, [name]: value }));
      
      if (value.length > 0) {
        const passwordError = validatePassword(value);
        if (passwordError) {
          setErrors(prev => ({ ...prev, password: passwordError }));
        } else {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.password;
            return newErrors;
          });
        }
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.password;
          return newErrors;
        });
      }
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error on change
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Check if mobile exists and send OTP
  const handleCheckMobileAndSendOtp = async () => {
    const mobileError = validateMobileNumber(formData.mobileNumber);
    if (mobileError) {
      setErrors(prev => ({ ...prev, mobileNumber: mobileError }));
      return;
    }

    setOtpLoading(true);
    try {
      const response = await API.post(`/auth/is-existing-user`, {
        mobileNumber: formData.mobileNumber,
        countryCode: formData.countryCode,
        groupBotId: "1844",
        isRegister: true
      });

      const data = response.data;
      if (data.status !== "RS_OK") {
        toast.error(data.errorDescription || 'Failed to verify mobile number');
        setErrors(prev => ({ ...prev, mobileNumber: data.errorDescription || 'Verification failed' }));
      } else if (data.isExistingUser) {
        // Mobile number already registered
        setErrors(prev => ({ ...prev, mobileNumber: 'Mobile number already registered' }));
      } else {
        // User doesn't exist, OTP sent - show OTP field
        toast.success('OTP sent successfully!');
        setOtpToken(data.token);
        setTimeRemaining(90);
        // Clear any mobile errors
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.mobileNumber;
          return newErrors;
        });
      }
    } catch (error) {
      console.error("Failed to check mobile:", error);
      toast.error('An error occurred while verifying mobile number');
    } finally {
      setOtpLoading(false);
    }
  };

  // OTP input handlers
  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { value } = e.target;
    if (!/^[0-9]*$/.test(value)) return;
    
    const chars = value.split("");
    const newOtp = [...otp];
    let nextIndex = index;
    
    for (let i = 0; i < chars.length && nextIndex < OTP_LENGTH; i++) {
      newOtp[nextIndex] = chars[i];
      nextIndex++;
    }
    setOtp(newOtp);
    
    if (chars.length > 0 && nextIndex < OTP_LENGTH) {
      inputsRef.current[nextIndex]?.focus();
    }
  };

  const handleOtpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputsRef.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasteData) return;
    
    const newOtp = [...otp];
    for (let i = 0; i < pasteData.length; i++) {
      newOtp[i] = pasteData[i];
    }
    setOtp(newOtp);
    
    const nextIndex = Math.min(pasteData.length, OTP_LENGTH - 1);
    inputsRef.current[nextIndex]?.focus();
  };

  // Form submission - Verify OTP and Register
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const usernameError = validateUsername(formData.username);
    const passwordError = validatePassword(formData.password);
    const mobileError = validateMobileNumber(formData.mobileNumber);
    
    const newErrors: Record<string, string> = {};
    if (usernameError) newErrors.username = usernameError;
    if (passwordError) newErrors.password = passwordError;
    if (mobileError) newErrors.mobileNumber = mobileError;
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Check if username is available
    if (usernameAvailable !== true) {
      toast.error('Please choose an available UserId');
      return;
    }

    // Check if OTP was sent
    if (!otpToken) {
      toast.error('Please send OTP first');
      return;
    }

    // Check if OTP is complete
    const otpValue = otp.join("");
    if (otpValue.length !== OTP_LENGTH) {
      setErrors(prev => ({ ...prev, otp: 'Please enter complete OTP' }));
      return;
    }

    if (timeRemaining <= 0) {
      setErrors(prev => ({ ...prev, otp: 'OTP has expired. Please request a new one.' }));
      return;
    }

    setLoading(true);
    
    try {
      // First verify OTP
      const verifyResponse = await API.post(`/auth/verify-otp`, {
        otp: otpValue,
        token: otpToken,
      });

      if (verifyResponse.data.status !== "RS_OK") {
        toast.error(verifyResponse.data.errorDescription || 'OTP verification failed');
        setLoading(false);
        return;
      }

      // Then signup
      const signupResponse = await API.post(`/auth/signup`, {
        token: otpToken,
        password: formData.password,
        userId: formData.username,
        promoCode: formData.campaignCode,
        role: "USER"
      });

      const data = signupResponse.data;
      if (data.status !== "RS_OK") {
        toast.error(data.errorDescription || 'Registration failed');
      } else {
        sessionStorage.setItem("userKey", data.userKey);
        sessionStorage.setItem("token", data.accessToken);
        sessionStorage.setItem("rtoken", data.refreshToken);
        dispatch(setIsLoggedIn(true));
        dispatch(setBrowser("Web"));
        toast.success('Account created successfully!');
        navigate("/game");
      }
    } catch (error) {
      console.error("Registration failed:", error);
      toast.error('An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (timeRemaining > 0) return;
    await handleCheckMobileAndSendOtp();
    setOtp(Array(OTP_LENGTH).fill(""));
    inputsRef.current[0]?.focus();
  };

  const isOtpComplete = otp.every((val) => val !== "");

  return (
    <div className={`w-full max-w-md mx-auto p-6 pt-8 ${containerBg} text-white ${containerRounded} shadow-lg`}>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-white">Sign Up</h2>
        <p className={`text-sm ${descriptionTextClass}`}>
          Create your account by following these simple steps.
        </p>
      </div>

      {/* Telegram Promo Box */}
      <div className={`rounded-lg ${isLovable ? 'bg-[hsl(275,35%,20%)]' : 'bg-zinc-800 border border-zinc-700'} mb-4`}>
        <a
          href={telegramLinkWithReferral}
          target="_blank"
          rel="noopener noreferrer"
          className="no-underline"
        >
          <div className={`flex items-center gap-1.5 sm:gap-2 p-2 sm:p-3 ${isLovable ? 'bg-[hsl(275,35%,20%)] hover:bg-[hsl(275,35%,25%)]' : 'bg-zinc-700 hover:bg-zinc-600 border border-zinc-600'} rounded-md cursor-pointer transition-colors`}>
            <FaTelegramPlane className={`w-4 h-4 sm:w-5 sm:h-5 shrink-0 ${linkClass}`} />
            <span className={`text-xs sm:text-sm ${linkClass}`}>
              Try out our site and play on Telegram
            </span>
          </div>
        </a>
      </div>

      {/* Form Container */}
      <div className={`p-4 rounded-lg ${innerBg} border`}>
        <form onSubmit={handleSubmit} className="flex flex-col w-full">
            {/* Username Field */}
            <label className={`text-sm ${descriptionTextClass} mb-1`}>
              UserId <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                name="username"
                placeholder="Enter userId (min 5 characters)"
                value={formData.username}
                onChange={handleChange}
                className={`p-2 pr-10 w-full border rounded-md text-sm ${inputClass} focus:outline-none focus:ring-2 ${errors.username ? 'border-red-500' : usernameAvailable === true ? 'border-green-500' : ''}`}
              />
              {/* Status indicator */}
              <div className="absolute inset-y-0 right-2 flex items-center">
                {usernameChecking && (
                  <svg className="animate-spin h-4 w-4 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {!usernameChecking && usernameAvailable === true && (
                  <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                )}
                {!usernameChecking && usernameAvailable === false && (
                  <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                )}
              </div>
            </div>
            {errors.username && (
              <span className="text-red-500 text-xs mt-1">{errors.username}</span>
            )}
            {!errors.username && usernameAvailable === true && (
              <span className="text-green-500 text-xs mt-1">UserId available!</span>
            )}

            {/* Password Field */}
            <label className={`text-sm ${descriptionTextClass} mb-1 mt-4`}>
              Password <span className="text-red-500">*</span>
            </label>
            <div className="relative w-full">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Enter password"
                value={formData.password}
                onChange={handleChange}
                className={`p-2 pr-10 w-full border rounded-md text-sm ${inputClass} focus:outline-none focus:ring-2 ${errors.password ? 'border-red-500' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute inset-y-0 right-2 flex items-center ${iconClass}`}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                )}
              </button>
            </div>
            {errors.password && (
              <span className="text-red-500 text-xs mt-1">{errors.password}</span>
            )}

            {/* Campaign Code Field */}
            <label className={`text-sm ${descriptionTextClass} mb-1 mt-4`}>
              Campaign Code
            </label>
            <input
              type="text"
              name="campaignCode"
              placeholder="Enter Campaign Code"
              value={formData.campaignCode}
              onChange={handleChange}
              disabled={!!referalCode}
              className={`p-2 w-full border rounded-md text-sm bg-zinc-800 text-white border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${referalCode ? 'opacity-60 cursor-not-allowed' : ''}`}
            />

            {/* Phone Number / WhatsApp */}
            <label className={`flex items-center gap-2 text-sm ${descriptionTextClass} mb-1 mt-4`}>
              Phone Number 
            </label>
            <div className="flex items-center gap-1">
              <span className={`px-2 py-2 text-sm ${countryCodeClass} border rounded-md shrink-0`}>
                +{formData.countryCode}
              </span>
              <input
                type="number"
                name="mobileNumber"
                placeholder="Mobile number"
                value={formData.mobileNumber}
                onChange={handleChange}
                className={`p-2 min-w-0 flex-1 border rounded-md text-sm ${inputClass} focus:outline-none focus:ring-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${errors.mobileNumber ? 'border-red-500' : ''}`}
              />
              <button
                type="button"
                onClick={handleCheckMobileAndSendOtp}
                disabled={otpLoading || formData.mobileNumber.length !== 10 || timeRemaining > 0}
                className={`px-2 py-2 text-xs ${primaryBtnClass} rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shrink-0`}
              >
                {otpLoading ? '...' : timeRemaining > 0 ? `${timeRemaining}s` : 'Send OTP'}
              </button>
            </div>
            {errors.mobileNumber && (
              <span className="text-red-500 text-xs mt-1">{errors.mobileNumber}</span>
            )}

            {/* OTP Input - Shows after OTP is sent */}
            {otpToken && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className={`text-sm ${descriptionTextClass}`}>
                    Enter OTP <span className="text-red-500">*</span>
                  </label>
                  <span className={`text-xs ${timeRemaining > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {timeRemaining > 0 
                      ? `Expires in ${Math.floor(timeRemaining / 60)}:${(timeRemaining % 60).toString().padStart(2, '0')}`
                      : 'OTP expired'
                    }
                  </span>
                </div>
                <div className="flex justify-center gap-2">
                  {otp.map((value, index) => (
                    <input
                      key={index}
                      type="number"
                      maxLength={OTP_LENGTH}
                      value={value}
                      onChange={(e) => handleOtpChange(e, index)}
                      onKeyDown={(e) => handleOtpKeyDown(e, index)}
                      onPaste={handleOtpPaste}
                      ref={(el) => { inputsRef.current[index] = el; }}
                      className={`w-9 h-11 text-center text-lg font-bold border rounded-md 
                        ${value ? otpFilledClass : otpInputClass} 
                        focus:outline-none focus:ring-2 ${isLovable ? 'focus:ring-[hsl(275,90%,64%)]' : 'focus:ring-blue-500'} text-white
                        [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                    />
                  ))}
                </div>
                {errors.otp && (
                  <span className="text-red-500 text-xs mt-1 block text-center">{errors.otp}</span>
                )}
                {timeRemaining <= 0 && (
                  <div className="text-center mt-2">
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      className={`${linkClass} text-xs hover:underline`}
                    >
                      Resend OTP
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Sign in link */}
            <div className="text-center mt-4">
              <span className={`${descriptionTextClass} text-sm`}>Have an account? </span>
              <button
                type="button"
                onClick={handleSignInClick}
                className={`${linkClass} text-sm hover:underline`}
              >
                Sign in
              </button>
            </div>

            {/* Sign Up Button */}
            <button
              type="submit"
              disabled={loading || !otpToken || !isOtpComplete || timeRemaining <= 0 || usernameAvailable !== true || usernameChecking}
              className={`p-2.5 mt-4 w-full text-sm ${primaryBtnClass} rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Account...
                </>
              ) : (
                'SIGN UP'
              )}
            </button>
          </form>
      </div>
    </div>
  );
};

export default RegisterPage;
